import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HTMLParser from 'html-react-parser';
import {
  ContactPrivacySettingsAPIPropertiesModel,
  NotificationPreferenceDto,
  ConsentDto,
  ConsentTypeContentResponseModel,
} from '@/services/platform/api/model';
import { useNotifications } from '@/services/platform/data/profile/notifications';
import { useConsents, useUpdateConsents } from '@/services/platform/data/profile/consents';
import { useConsentItems } from '@/services/platform/data/cms/useConsentItems';
import { Icon } from '@/components/ui/icon';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { EditContactSettingsSheet } from '@/components/shared/sheets/edit-contact-settings';
import { ScreenHead } from '@/components/shared/screen/head';
import { Switch } from '@/components/ui/switch';
import { useClientTranslation } from '@/lib/core';
import { useApp } from '@/lib/core/providers/appProvider';
import { useLocaleStore } from '@/lib/core/stores';

interface ContactPrivacySettingsPageProps {
  content: { properties?: unknown };
}

export function ContactPrivacySettingsPage({ content }: ContactPrivacySettingsPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useClientTranslation();

  const properties = content.properties as ContactPrivacySettingsAPIPropertiesModel | undefined;

  const [view, setView] = useState<'main' | 'customise'>('main');
  const [isEditContactOpen, setIsEditContactOpen] = useState(false);
  const [customiseDraft, setCustomiseDraft] = useState<Record<string, boolean>>({});

  const { appSettings } = useApp();
  const privacyPolicyPath = appSettings?.properties?.privacyPolicy?.route?.path;
  const locale = useLocaleStore(state => state.locale);

  const {
    data: notificationData,
    isLoading: isNotificationLoading,
    refetch: refetchNotifications,
  } = useNotifications();
  const { data: consentData, isLoading: isConsentLoading, refetch: refetchConsents } = useConsents();
  const { mutateAsync: updateConsents, isPending: isUpdatingConsents } = useUpdateConsents();

  const notifications: NotificationPreferenceDto[] =
    notificationData?.status === 200 ? (notificationData.data ?? []) : [];
  const consents: ConsentDto[] = consentData?.status === 200 ? (consentData.data ?? []) : [];

  const contactItems = properties?.contactConsentsPicker ?? [];
  const personalDataItems = properties?.personalDataConsentPicker ?? [];
  const privacyItems = properties?.privacySettingsConsentPicker ?? [];

  const consentItemIds = [...personalDataItems.map(i => i.id), ...privacyItems.map(i => i.id)].filter(
    (id): id is string => !!id
  );

  const { data: enrichedConsentItems } = useConsentItems(consentItemIds, locale);

  const enrichedMap = new Map(enrichedConsentItems?.map(i => [i.id, i]) ?? []);

  const personalDataItem = personalDataItems[0]
    ? (enrichedMap.get(personalDataItems[0].id ?? '') ?? personalDataItems[0])
    : undefined;
  const personalDataConsent = personalDataItem
    ? consents.find(c => c.consentTypeNodeKey === personalDataItem.id)
    : undefined;

  function enterCustomise() {
    const initial: Record<string, boolean> = {};
    for (const item of privacyItems) {
      const consent = consents.find(c => c.consentTypeNodeKey === item.id);
      initial[item.id ?? ''] = consent?.isOptedIn ?? false;
    }
    setCustomiseDraft(initial);
    setView('customise');
  }

  useEffect(() => {
    if (isConsentLoading) return;
    if (location.hash !== '#cookies') return;
    enterCustomise();
  }, [isConsentLoading]);

  async function handlePersonalDataToggle() {
    if (!personalDataItem?.id) return;
    await updateConsents({
      data: {
        consents: [
          { consentTypeNodeKey: personalDataItem.id, isOptedIn: !(personalDataConsent?.isOptedIn ?? false) },
        ],
      },
    });
    refetchConsents();
  }

  async function handleAcceptAll() {
    const payload = privacyItems.map(item => ({
      consentTypeNodeKey: item.id ?? '',
      isOptedIn: true,
    }));
    await updateConsents({ data: { consents: payload } });
    refetchConsents();
  }

  async function handleRejectAll() {
    const payload = privacyItems
      .filter(item => {
        const enriched = enrichedMap.get(item.id ?? '') ?? item;
        const typed = enriched as ConsentTypeContentResponseModel;
        const label = typed.properties?.label ?? item.name ?? '';
        return !(typed.properties?.alwaysActive ?? label.toLowerCase().includes('always active'));
      })
      .map(item => ({ consentTypeNodeKey: item.id ?? '', isOptedIn: false }));
    await updateConsents({ data: { consents: payload } });
    refetchConsents();
  }

  async function handleSaveChoices() {
    const payload = Object.entries(customiseDraft).map(([consentTypeNodeKey, isOptedIn]) => ({
      consentTypeNodeKey,
      isOptedIn,
    }));
    await updateConsents({ data: { consents: payload } });
    refetchConsents();
    setView('main');
  }

  function handleCustomiseAcceptAll() {
    const next: Record<string, boolean> = {};
    for (const item of privacyItems) {
      next[item.id ?? ''] = true;
    }
    setCustomiseDraft(next);
  }

  function handleCustomiseRejectAll() {
    const next: Record<string, boolean> = {};
    for (const item of privacyItems) {
      const enriched = enrichedMap.get(item.id ?? '') ?? item;
      const typed = enriched as ConsentTypeContentResponseModel;
      const label = typed.properties?.label ?? item.name ?? '';
      const isAlways = typed.properties?.alwaysActive ?? label.toLowerCase().includes('always active');
      next[item.id ?? ''] = isAlways ? true : false;
    }
    setCustomiseDraft(next);
  }

  const isLoading = isNotificationLoading || isConsentLoading;
  const pageTitle = t('App.Labels.ContactPrivacySettings');
  const customiseTitle = t('App.Labels.CustomiseSettings');

  if (view === 'customise') {
    return (
      <div className="pt-safe-area pb-safe-area relative flex h-full w-full flex-col">
        {/* Scrollable content */}
        <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-4 pb-6">
          <ScreenHead
            title={customiseTitle}
            onBack={() => setView('main')}
            ariaLabel={t('App.Labels.GoBack')}
            className="px-0 pt-0! pb-0"
          />
          {/* Info banner: title (bold) + disclaimer body, both inside the #f4f4f4 box */}
          {(properties?.customiseSettingsTitle || properties?.customiseSettingsDisclaimer?.markup) && (
            <div className="flex flex-col gap-0.5 bg-[#f4f4f4] px-4 py-2 text-[12px] leading-normal tracking-[0.5px]">
              {properties.customiseSettingsTitle && (
                <p className="font-pp-neue-montreal font-semibold text-[#1a1a1a]">
                  {properties.customiseSettingsTitle}
                </p>
              )}
              {properties.customiseSettingsDisclaimer?.markup && (
                <div className="font-pp-neue-montreal text-[#848484]">
                  {HTMLParser(properties.customiseSettingsDisclaimer.markup)}
                </div>
              )}
            </div>
          )}

          {/* Accept / Reject buttons */}
          <div className="flex gap-2">
            <button
              type="button"
              className="font-pp-neue-montreal h-11.5 flex-1 rounded-[2px] bg-[#1a1a1a] px-4 text-[12px] font-semibold tracking-[0.5px] text-white uppercase"
              onClick={handleCustomiseAcceptAll}
            >
              {t('App.Labels.AcceptAll')}
            </button>
            <button
              type="button"
              className="font-pp-neue-montreal h-11.5 flex-1 rounded-[2px] bg-[#1a1a1a] px-4 text-[12px] font-semibold tracking-[0.5px] text-white uppercase"
              onClick={handleCustomiseRejectAll}
            >
              {t('App.Labels.RejectAll')}
            </button>
          </div>

          {/* Cookie list */}
          <div className="flex flex-col">
            {privacyItems.map(item => {
              const enriched = enrichedMap.get(item.id ?? '') ?? item;
              const typed = enriched as ConsentTypeContentResponseModel;
              const label = typed.properties?.label ?? item.name ?? '';
              const isAlwaysActive =
                typed.properties?.alwaysActive ?? label.toLowerCase().includes('always active');
              const description = typed.properties?.description?.markup;
              const nodeKey = item.id ?? '';
              return (
                <div key={nodeKey} className="py-4 not-last:border-b-[0.5px] not-last:border-[#dfdfdf]">
                  <Switch
                    checked={isAlwaysActive ? true : (customiseDraft[nodeKey] ?? false)}
                    onCheckedChange={val => {
                      if (!isAlwaysActive) setCustomiseDraft(prev => ({ ...prev, [nodeKey]: val }));
                    }}
                    disabled={isAlwaysActive}
                    label={
                      <span
                        className={
                          isAlwaysActive
                            ? 'font-inter font-medium text-[#666]'
                            : 'font-pp-neue-montreal text-[#1a1a1a]'
                        }
                      >
                        {label}
                        {isAlwaysActive ? ` (${t('App.Labels.AlwaysActive')})` : ''}
                      </span>
                    }
                    description={description ? HTMLParser(description) : undefined}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 flex-col gap-2 border-t-[0.5px] border-[#c8c8c8] px-4 pt-6 pb-10.5">
          <button
            type="button"
            disabled={isUpdatingConsents}
            className="font-pp-neue-montreal h-11.5 w-full rounded-[2px] bg-[#1a1a1a] px-4 text-[12px] font-semibold tracking-[0.5px] text-white uppercase disabled:opacity-60"
            onClick={handleSaveChoices}
          >
            {t('App.Labels.SaveChoices')}
          </button>
          <button
            type="button"
            className="font-pp-neue-montreal h-9 w-full text-center text-[12px] tracking-[0.5px] text-[#1a1a1a] uppercase"
            onClick={() => {
              if (privacyPolicyPath) navigate(privacyPolicyPath);
            }}
          >
            {t('App.Labels.SeeOurPrivacyPolicy')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="pt-safe-area pb-safe-area relative flex h-full w-full flex-col">
        {isLoading ? (
          <SpinLoading wrapperClassName="min-h-40" />
        ) : (
          <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-4 pb-12">
            <ScreenHead
              title={pageTitle}
              onBack={() => navigate(-1)}
              ariaLabel={t('App.Labels.GoBack')}
              className="px-0 pt-0! pb-0"
            />
            {/* Contact settings */}
            {contactItems.length > 0 && (
              <section className="flex flex-col gap-2 border-b-[0.5px] border-[#dfdfdf] pb-4">
                <div className="flex items-center gap-2.5">
                  <p className="font-pp-neue-montreal min-w-0 flex-1 text-[20px] font-semibold text-[#1a1a1a]">
                    {properties?.contactSettingsTitle}
                  </p>
                  <button
                    type="button"
                    aria-label={t('App.Labels.EditContactSettings')}
                    className="flex size-11 shrink-0 items-center justify-center"
                    onClick={() => setIsEditContactOpen(true)}
                  >
                    <Icon name="edit-pencil" width={24} height={24} className="text-[#1a1a1a]" />
                  </button>
                </div>
                <div className="flex flex-col gap-2.5">
                  {contactItems.map(item => {
                    const notification = notifications.find(n => n.notificationTypeNodeKey === item.id);
                    return (
                      <div key={item.id} className="flex items-center gap-2.5">
                        <span className="font-pp-neue-montreal min-w-0 flex-1 text-[14px] leading-3.5 tracking-[0.5px] text-[#666]">
                          {item.name}
                        </span>
                        <span className="font-pp-neue-montreal shrink-0 text-right text-[14px] leading-3.5 tracking-[0.5px] text-[#1a1a1a]">
                          {notification?.isEnabled ? t('App.Labels.Yes') : t('App.Labels.No')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Personal data sharing */}
            {personalDataItem &&
              (() => {
                const typed = personalDataItem as ConsentTypeContentResponseModel;
                const pdLabel = typed.properties?.label ?? personalDataItem.name ?? '';
                const pdDescription = typed.properties?.description;
                const pdDescMarkup = pdDescription?.markup;
                const pdDescFallback = null;

                return (
                  <section className="flex flex-col gap-2 border-b-[0.5px] border-[#dfdfdf] pb-4">
                    <p className="font-pp-neue-montreal text-[20px] font-semibold text-[#1a1a1a]">
                      {properties?.personalDataSharingTitle}
                    </p>
                    <div className="flex items-start gap-2">
                      <Switch
                        checked={personalDataConsent?.isOptedIn ?? false}
                        onCheckedChange={() => handlePersonalDataToggle()}
                      />
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <p className="text-[14px] leading-3.5 font-medium tracking-[0.5px] text-[#1a1a1a]">
                          {pdLabel}
                        </p>
                        {pdDescMarkup ? (
                          <div className="font-pp-neue-montreal text-[12px] leading-normal tracking-[0.5px] text-[#848484]">
                            {HTMLParser(pdDescMarkup)}
                          </div>
                        ) : pdDescFallback ? (
                          <p className="font-pp-neue-montreal text-[12px] leading-normal tracking-[0.5px] text-[#848484]">
                            {pdDescFallback}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </section>
                );
              })()}

            {/* Privacy settings */}
            {privacyItems.length > 0 && (
              <section className="flex flex-col gap-2 border-b-[0.5px] border-[#dfdfdf] pb-4">
                <p className="font-pp-neue-montreal text-[20px] font-semibold text-[#1a1a1a]">
                  {properties?.privacySettingsTitle}
                </p>
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-2.5">
                    {privacyItems.map(item => {
                      const enriched = enrichedMap.get(item.id ?? '') ?? item;
                      const typed = enriched as ConsentTypeContentResponseModel;
                      const label = typed.properties?.label ?? item.name ?? '';
                      const consent = consents.find(c => c.consentTypeNodeKey === item.id);
                      return (
                        <div key={item.id} className="flex items-center gap-2.5">
                          <span className="font-pp-neue-montreal min-w-0 flex-1 text-[14px] leading-3.5 tracking-[0.5px] text-[#666]">
                            {label}
                          </span>
                          <span className="font-pp-neue-montreal shrink-0 text-right text-[14px] leading-3.5 tracking-[0.5px] text-[#1a1a1a]">
                            {consent?.isOptedIn ? t('App.Labels.Yes') : t('App.Labels.No')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      disabled={isUpdatingConsents}
                      className="font-pp-neue-montreal h-11.5 w-full rounded-[2px] bg-[#1a1a1a] px-4 text-[12px] font-semibold tracking-[0.5px] text-white uppercase"
                      onClick={handleAcceptAll}
                    >
                      {properties?.acceptAllCookiesBtn ?? t('App.Labels.AcceptAll')}
                    </button>
                    <button
                      type="button"
                      disabled={isUpdatingConsents}
                      className="font-pp-neue-montreal h-11.5 w-full rounded-[2px] bg-[#1a1a1a] px-4 text-[12px] font-semibold tracking-[0.5px] text-white uppercase"
                      onClick={handleRejectAll}
                    >
                      {properties?.rejectCookiesBtn ?? t('App.Labels.RejectAll')}
                    </button>
                    <button
                      type="button"
                      className="font-pp-neue-montreal h-11.5 w-full rounded-[2px] border-[0.5px] border-[#1a1a1a] px-4 text-[12px] font-semibold tracking-[0.5px] text-[#1a1a1a] uppercase"
                      onClick={enterCustomise}
                    >
                      {properties?.customiseSettingsBtn ?? t('App.Labels.SaveChoices')}
                    </button>
                  </div>
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      <EditContactSettingsSheet
        open={isEditContactOpen}
        onOpenChange={setIsEditContactOpen}
        notifications={notifications}
        items={contactItems}
        onSaved={() => refetchNotifications()}
      />
    </>
  );
}
